import Vue from 'vue';
import { ChatService } from '@/services/chat.service';

const chatState = {
  chatname: '',
  chat: {},
  scrollLock: false,
  sessions: {},
  userlistInterval: null,
};

const chatMutations = {
  /**
   * Set the users chatname in store
   * @param {Object} state
   * @param {String} chatname
   */
  setChatname(state, chatname) {
    state.chatname = chatname;
  },
  /**
   * Remove a chat from state
   * @param {Object} state
   * @param {String} id
   */
  clearChat(state, id) {
    state.scrollLock = false;
    delete state.chat[id];
  },
  /**
   * Add a chat in state
   * @param {Object} state
   * @param {String} id
   */
  createChat(state, id) {
    if (!state.chat[id]) {
      Vue.set(state.chat, id, {
        pinned: {},
        messages: [],
        userlist: [],
        replay: {
          original: [],
          future: [],
        },
      });
    }
  },
  setPinned(state, { id, message, type }) {
    Vue.set(state.chat[id].pinned, type, message);
  },
  /**
   * Add message to chat messagelist and remove first if max
   * number of messages has been reached
   * @param {Object} state
   * @param {Object} param1
   * @param {String} param1.id
   * @param {Object} param1.message
   */
  addMessage(state, { id, message }) {
    const existIndex = state.chat[id].messages.findIndex(m => m.message_id === message.message_id);

    if (existIndex >= 0) {
      return;
    }

    state.chat[id].messages.push(message);
  },
  setUserlist(state, { id, list }) {
    if (!state.chat[id]) {
      return;
    }

    state.chat[id].userlist = list;
  },
  /**
   * Set sessionID for BroadcastID in state
   * @param {Object} state
   * @param {Object} param1
   * @param {String} param1.broadcastId
   * @param {String} param1.sessionId
   */
  setSession(state, { broadcastId, sessionId, userId }) {
    state.sessions[broadcastId] = {
      sessionId,
      userId,
    };
  },
  /**
   * Removes a message in chat
   * @param {Object} state
   * @param {String} chatId
   * @param {String} messageId
   */
  removeMessage(state, { chatId, messageId }) {
    if (!state.chat[chatId]) {
      return;
    }

    const { userId } = state.sessions[chatId];
    const index = state.chat[chatId].messages.findIndex(m => m.message_id === messageId && m.user_id !== userId);
    if (index >= 0) {
      state.chat[chatId].messages.splice(index, 1);
    }
  },
  removeMessageByPayloadId(state, { chatId, payloadId }) {
    if (!state.chat[chatId]) {
      return;
    }

    const messages = state.chat[chatId].messages.filter(m => m.payload_id !== payloadId);
    state.chat[chatId].messages = messages;
  },
  /**
   * -- Inactive --
   * Removes all messages by userId
   * @param {Object} state
   * @param {String} chatId
   * @param {String} userId
   */
  removeMessagesByUserId(state, { chatId, userId }) {
    if (!state.chat[chatId]) {
      return;
    }
    const messages = state.chat[chatId].messages.filter(m => m.user_id !== userId);
    state.chat[chatId].messages = messages;
  },
  scrollLock(state, bool) {
    state.scrollLock = bool;
  },
};

const chatGetters = {
  /**
   * Return current chatname from state
   * @param {Object} state
   * @returns {String}
   */
  chatname: state => state.chatname,
  /**
   * Return current sessionID from state
   * @param {Object} state
   * @returns {String}
   */
  sessionId: state => broadcastId => (state.sessions[broadcastId] ? state.sessions[broadcastId].sessionId : null),
  /**
    * Return current userID from state
    * @param {Object} state
    * @returns {String}
    */
  userId: state => broadcastId => (state.sessions[broadcastId] ? state.sessions[broadcastId].userId : null),
  /**
   * Return list of messages for array with {ID}
   * @param {Object} state
   * @param {String} id
   * @returns {Array}
   */
  messages: state => id => (state.chat[id] ? state.chat[id].messages : []).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)),
  /**
   * Return list of users for chat with {ID}
   * @param {Object} state
   * @param {String} id
   * @returns {Array}
   */
  userlist: state => id => (state.chat[id] ? state.chat[id].userlist : []),
  /**
   * Scrollock
   */
  scrollLock: state => state.scrollLock,
  pinnedMessage: state => id => (state.chat[id] ? state.chat[id].pinned : null),
};

const chatActions = {
  getUserlist: async ({ state, commit }, { id, interval = false }) => {
    clearInterval(state.userlistInterval);
    const userlist = await ChatService.listUsers(id);
    commit('setUserlist', { id, list: userlist });

    if (interval) {
      state.userlistInterval = setInterval(async () => {
        const list = await ChatService.listUsers(id);
        commit('setUserlist', { id, list });
      }, 5000);
    }
  },
  clearUserlist: async ({ state }) => {
    clearInterval(state.userlistInterval);
  },
  scrollLock: ({ commit }, bool) => {
    commit('scrollLock', bool);
  },
  setUserlist: ({ commit, state }, { id, list }) => {
    if (!state.chat[id]) {
      return;
    }

    commit('setUserlist', { id, list });
  },
  /**
   * Handle incoming messages from Event-store
   * @param {Object} param0
   * @param {Function} param0.commit
   * @param {Object} param0.state
   * @param {Object} param1
   * @param {String} param1.id
   * @param {Object} param1.message
   */
  handleMessage: ({ commit, state }, { id, message }) => {
    if (!state.chat[id]) {
      commit('createChat', id);
    }
    if (message.event === 'chat.session') {
      commit('setSession', { broadcastId: id, sessionId: message.payload.session_id, userId: message.payload.user_id });
    }
    if (message.event === 'chat.message.deleted') {
      commit('removeMessage', { chatId: id, messageId: message.payload.id });
    }
    if (message.event === 'chat.pinned-message') {
      commit('setPinned', {
        id,
        message: message.payload.message,
        type: message.payload.type,
      });

      commit('addMessage', {
        id,
        message: {
          message_id: message.message_id,
          event: message.payload.type,
          timestamp: message.timestamp,
          message: message.payload.message,
        },
      });
    }
    if (message.event === 'chat.product') {
      const messageData = {
        message_id: message.id,
        event: 'chat.product',
        show: message.show,
        timestamp: message.timestamp,
        product: message.product,
      };

      commit('addMessage', {
        id,
        message: messageData,
      });
    }
    if (message.event === 'chat.message' || message.event === 'chat.message.reply') {
      let messageData = {
        message: message.payload.message,
        name: message.payload.name,
        message_id: message.message_id,
        timestamp: message.timestamp,
        relativeTime: message.relative_time,
        user_id: message.payload.user_id,
        session_id: message.payload.session_id,
        moderator: message.payload.moderator,
      };

      if (message.event === 'chat.message.reply') {
        messageData = {
          ...messageData,
          type: 'reply',
          reply_to: message.payload.reply_to,
          reply_to_message_id: message.payload.reply_to_message_id,
        };
      }

      commit('addMessage', {
        id,
        message: messageData,
      });
    }
  },
  /**
   * Handle the incoming chatname and commit it
   * @param {Object} obj
   * @param {Function} obj.commit
   * @param {String} chatname
   */
  setChatname: ({ commit }, chatname) => {
    if (chatname === '') {
      return false;
    }
    commit('setChatname', chatname);
    return true;
  },

};

export default {
  namespaced: true,
  state: chatState,
  mutations: chatMutations,
  getters: chatGetters,
  actions: chatActions,
};
